import React, { useCallback, useEffect, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
// Copied from app.run.js for fadeout effect
import $ from 'jquery';
import { Country } from '@bb/domain/dist/types';
import { Feature } from '@bb/automaton';
import { isUserLicensed, ROLES, Titles } from '@bb/users';
import { UserController, OnboardingController } from 'api';
import type { ApolloError } from '@apollo/client';
import queryString from 'query-string';

import Swal from 'sweetalert2';
import errorReporter from '@app/errorReporter';
import Axios from 'axios';
import { useAppcues, usePusher, PusherInstance, useSearchParams } from 'hooks';
import {
  PusherChannelTypes,
  isLimitedAccessOnUnclaimedBrokerage,
  DASHBOARD_LISTING_STORAGE_KEYS,
  MLS_STAFF_USERS_STORAGE_KEYS,
  SHOWINGS_CALENDAR_STORAGE_KEYS,
  featureFlagClient,
  featureEnabled,
} from 'utils';
import { buildUserTitles } from '@app/helpers';
import type { Plan, CachedOnboardingPortal } from '@bb/automaton';
import { getPortalAnsweringConfig } from 'api/AnsweringSubscriptionController';
import csrfTokenService from '@app/helpers/csrfTokenService';
import {
  getSubscriptionOptionsForOrg,
  SupraSubscriptionOptionsType,
} from 'api/SupraController';
import { findSupraSystemSubscription } from 'api/SupraSystemSubscriptionController';
import { getOffices, GetOfficesDTO } from 'api/OrgController';
import type { JsonObject } from '@/app-package';
import { PortalAnsweringConfig } from 'api/AnsweringSubscriptionController/getPortalAnsweringConfig';
import {
  OrgStatus,
  SHOW_WELCOME_URL_PARAM,
} from '../Router/onboarding/constants';
import { adminsHomePage, agentsHomePage } from '../Router/constants';
import { GetOnboarding } from '../types/getOnboardingType';
import { AuthContextUser } from './types';

type OnboardingPortalConfig = JsonObject<CachedOnboardingPortal> & {
  titles: Record<Titles, { label: string; value: Titles }>;
};

export type AuthContextType = {
  /** The logged-in user or the user being assumed */
  auth: AuthContextUser;
  pusher?: PusherInstance;
  setPusherEvents?: (
    channelType: PusherChannelTypes,
    eventName: string,
    cb: (arg: any) => void,
    isBind?: boolean,
  ) => void;
  setAuth: React.Dispatch<React.SetStateAction<AuthContextType['auth']>>;
  logout: () => void;
  toggleChatInactive: (callback?: () => void) => Promise<boolean | undefined>;
  isBrokerBay: boolean;
  orgStatus: OrgStatus | null;
  orgPlan: JsonObject<Plan> | null;
  /** The Onboarding object for this user's org */
  onboarding?: GetOnboarding['onboarding'] | undefined;
  offices: GetOfficesDTO;
  fetchOrgStatus: () => Promise<void>;
  fetchAuth: () => Promise<void>;
  fetchingAuth: boolean;
  fetchOnboarding: () => Promise<void>;
  /** The configurations for the onboarding portal of a user's org */
  onboardingPortalConfig: OnboardingPortalConfig;
  /** The Answering Service configurations taken from the
   *  Onboarding Portal that this user's brokerage is under */
  answeringPortalConfig: PortalAnsweringConfig | undefined;
  /** Any error encountered while trying to fetch the onboarding portal configurations */
  answeringPortalConfigError: ApolloError | undefined;
  /** Whether the onboarding portal configurations are still being fetched */
  loadingAnsweringPortalConfig: boolean;
  /** Whether the Onboarding object is still being fetched */
  loadingOnboarding: boolean;
  hasFeature: (feature: Feature | `${Feature}`) => boolean;
  /** Users in an unclaimed org on the Brokerage Plan have limited access to certain features,
   * particularly listing-related features */
  hasLimitedAccess: boolean;
  isFromCountry: (country: Country) => boolean;
  /** Whether their MLS/board only pays for Agent Plan (Tier 1) but the
   * brokerage themselves are paying for Brokerage Plan (Tier 2) */
  isOrgPlanUpgraded: boolean;
  /** Whether the org is on the Brokerage Plan and it is paid for by
   * their MLS/board (not themselves) */
  isOrgOnBrokeragePlan: boolean;
  /** Whether this is an Unclaimed org on the Agent Plan */
  isUnclaimedOnAgentPlan: boolean;
  upgradePlanDataLoaded: boolean;
  orgSupraSubscriptionOptions?: SupraSubscriptionOptionsType;
};

const AuthContext = React.createContext<AuthContextType>({} as AuthContextType);

export const AuthContextProvider: React.FC = ({ children }) => {
  const [auth, setAuth] = React.useState<AuthContextType['auth']>();

  const [offices, setOffices] = React.useState<GetOfficesDTO>([]);

  const [answeringPortalConfig, setAnsweringPortalConfig] =
    React.useState<PortalAnsweringConfig>();

  const [loadingAnsweringPortalConfig, setLoadingAnsweringPortalConfig] =
    React.useState<boolean>(true);

  const [answeringPortalConfigError, setPortalAnsweringConfigError] =
    React.useState<ApolloError>();

  const [loadingOnboarding, setLoadingOnboarding] = React.useState(true);
  const [fetchingAuth, setFetchingAuth] = React.useState(true);

  const [onboardingData, setOnboardingData] = React.useState<todo>();

  const [orgSupraSubscriptionOptions, setOrgSupraSubscriptionOptions] =
    React.useState<SupraSubscriptionOptionsType>();

  const hasFeature = useCallback(
    (feature: Feature | `${Feature}`) => {
      const isAssumed = Boolean(auth?.assumingUserId || auth?.isAssumedNotBB);
      /*
        If the user is being assumed, all features should be displayed,
        except for NETWORK, DEMO, SUPRA_MPV and PRE_LAUNCH
      */
      if (
        isAssumed &&
        !['NETWORK', 'DEMO', 'PRE_LAUNCH', 'SUPRA_MVP'].includes(feature)
      ) {
        return true;
      }

      return Boolean(auth?.org?.features.includes(feature));
    },
    [auth?.assumingUserId, auth?.isAssumedNotBB, auth?.org?.features],
  );

  const isFromCountry = (country: Country) => country === auth?.org?.country;

  const Appcues = useAppcues();
  const history = useHistory();
  const { sso } = useSearchParams();
  const hasChat = hasFeature(Feature.CHAT);
  const { pusher, setPusherEvents } = usePusher(auth?.id, hasChat);

  const [
    onboardingPortalConfig,
    setOnboardingPortalConfig,
    // @ts-expect-error fix me
  ] = React.useState<OnboardingPortalConfig>({ titles: buildUserTitles() });

  const logout = async () => {
    if (!auth) return;

    const { isChatAdmin, isChatInactive } = auth;
    if (isChatAdmin && !isChatInactive) {
      const { dismiss, value: confirm } = await Swal.fire({
        title: 'Chat Status',
        text: `By logging out your chat status will be set to AWAY.
          By doing so, agents that message you will be forwarded to other front-desk admins.`,
        type: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#71b8f6',
        confirmButtonText: 'Log Out',
        cancelButtonText: 'Cancel',
      });
      if (confirm) {
        await UserController.setChatStatus(true);
      } else if (dismiss) {
        return;
      }
    }

    Appcues.reset();
    window.localStorage.setItem('loggedOut', 'true');
    window.localStorage.removeItem('bbCrossService:accessToken');
    window.localStorage.removeItem('csrfToken');
    window.localStorage.removeItem(DASHBOARD_LISTING_STORAGE_KEYS.availability);
    window.localStorage.removeItem(DASHBOARD_LISTING_STORAGE_KEYS.status);
    window.localStorage.removeItem(
      MLS_STAFF_USERS_STORAGE_KEYS.userTypesFilter,
    );
    window.localStorage.removeItem(
      MLS_STAFF_USERS_STORAGE_KEYS.supraStatusesFilter,
    );
    window.localStorage.removeItem(
      MLS_STAFF_USERS_STORAGE_KEYS.userStatusesFilter,
    );
    window.localStorage.removeItem(
      MLS_STAFF_USERS_STORAGE_KEYS.answeringServiceTypesFilter,
    );
    window.localStorage.removeItem(
      SHOWINGS_CALENDAR_STORAGE_KEYS.buyerStatuses,
    );
    window.localStorage.removeItem(
      SHOWINGS_CALENDAR_STORAGE_KEYS.buyerPendingStatus,
    );
    window.localStorage.removeItem(
      SHOWINGS_CALENDAR_STORAGE_KEYS.listingStatuses,
    );
    window.localStorage.removeItem(
      SHOWINGS_CALENDAR_STORAGE_KEYS.listingPendingStatus,
    );
    window.localStorage.removeItem(
      SHOWINGS_CALENDAR_STORAGE_KEYS.individualListingStatuses,
    );
    window.localStorage.removeItem(
      SHOWINGS_CALENDAR_STORAGE_KEYS.individualListingPendingStatus,
    );
    // We can't use react-router here because this route is outside the hash (/#/) routes
    window.location.href = '/logoutpage';
  };

  const toggleChatInactive = (onRequestSend: () => void) => {
    const sendRequest = async () => {
      const isChatInactive = !auth?.isChatInactive;
      if (onRequestSend) onRequestSend();
      await UserController.setChatStatus(isChatInactive);
      setAuth(oldAuth => ({ ...oldAuth, isChatInactive }));

      const angularRootScope = angular
        .element(document)
        .injector()
        .get('$rootScope');
      angularRootScope.$apply(() => {
        // @ts-expect-error fix me
        angularRootScope.auth.isChatInactive = isChatInactive;
      });

      return isChatInactive;
    };
    const openModal = () =>
      Swal.fire({
        title: 'Chat Status',
        text: `Would you like to set your chat status as AWAY?
          By doing so, agents that message you will be forwarded to other front-desk admins.`,
        type: 'warning',
        showCancelButton: true,
        cancelButtonColor: '#707070',
        confirmButtonColor: '#4974C5',
        confirmButtonText: "Yes, I'm away",
        reverseButtons: true,
      }).then(({ value }) => {
        if (value) return sendRequest();
        return auth?.isChatInactive;
      });

    if (auth?.isChatInactive) return sendRequest();
    return openModal();
  };

  const fetchAuth = useCallback(async () => {
    setFetchingAuth(true);
    try {
      const retrievedUser = await UserController.current({
        sso: sso === 'true',
      });

      if ('notAuthenticated' in retrievedUser) {
        if (window.location.href.indexOf('redirect_login') === -1) {
          const url = window.location.href.split('#')[1];
          window.location.replace(`/redirect_login#${url}`);
          return;
        }
        return;
      }
      window.localStorage.removeItem('loggedOut');
      const isCypress = window.localStorage.getItem('isCypress') === 'true';

      const cleanedUser: AuthContextUser = {
        ...retrievedUser,
        roles: ROLES,
        isLicensed: isUserLicensed(retrievedUser),
        isCypress,
      };

      if (ROLES[cleanedUser.role] >= ROLES.BRANCH_ADMIN) {
        cleanedUser.isAdmin = true;
      }

      if (sso === 'true' && cleanedUser.isClaimed === false) {
        const result = await OnboardingController.initiateSSOClaimProfile({
          currentLocation: window.location.href,
        });
        window.location.replace(result.redirect);
        return;
      }

      setAuth(cleanedUser);

      const fetchedOffices = await getOffices();
      setOffices(fetchedOffices);

      const { onboardingPortal } = retrievedUser.org;

      if (onboardingPortal) {
        setOnboardingPortalConfig({
          ...onboardingPortal,
          titles: buildUserTitles(onboardingPortal),
        });
        setLoadingAnsweringPortalConfig(false);
      }

      const angularRootScope = angular
        .element(document)
        .injector()
        .get('$rootScope');
      angularRootScope.$apply(() => {
        // @ts-expect-error fix me
        Object.assign(angularRootScope.auth, cleanedUser);
      });

      if ((window as any).Rollbar) {
        (window as any).Rollbar.configure({
          payload: {
            person: {
              id: cleanedUser.id,
              email: cleanedUser.email,
              username: `${cleanedUser.firstName} ${cleanedUser.lastName}`,
            },
          },
        });
      }

      const { location } = history;
      if (
        // This will only be true the first time the user logs in to BrokerBay
        cleanedUser.isClaimed === false &&
        [adminsHomePage, agentsHomePage].includes(location.pathname)
      ) {
        const currentSearch = queryString.parse(location.search);
        const nextSearch = queryString.stringify({
          ...currentSearch,
          [SHOW_WELCOME_URL_PARAM]: true,
        });
        history.push({ pathname: location.pathname, search: `?${nextSearch}` });
      }
    } catch (err) {
      errorReporter.error(err);
      history.push('/');
    } finally {
      $('.loader-container').fadeOut(); // will first fade out the loading animation
      setFetchingAuth(false);
    }
  }, [history, sso]);

  useEffect(() => {
    const fetchAnsweringPortal = async () => {
      try {
        const foundAnsweringPortal =
          auth?.org.onboardingPortalId &&
          (await getPortalAnsweringConfig(
            auth?.org.onboardingPortalId,
            auth?.org.id,
          ));
        if (foundAnsweringPortal) {
          setAnsweringPortalConfig(foundAnsweringPortal);
          setLoadingAnsweringPortalConfig(false);
        }
      } catch (err) {
        errorReporter.error(err);
        setPortalAnsweringConfigError(err);
      }
    };

    if (auth?.org.onboardingPortalId) {
      fetchAnsweringPortal();
    } else {
      setLoadingAnsweringPortalConfig(false);
    }
  }, [auth]);

  useEffect(() => {
    const fetchSupraSubscription = async () => {
      if (!auth?.id || !auth.org?.id) {
        return;
      }

      const subscription = await findSupraSystemSubscription({
        userId: auth.id,
        orgId: auth.org.id,
      });

      setAuth(prev => ({ ...prev, supraSubscription: subscription }));
    };

    fetchSupraSubscription();
  }, [auth?.id, auth?.org?.id]);

  useEffect(() => {
    const getSupraOwnerSubscriptionOptions = async () => {
      if (
        featureEnabled('supraBillingApiFeature') &&
        ((auth.org.type === 'brokerage' && hasFeature(Feature.SUPRA_MVP)) ||
          auth.org.type === 'mlsStaff')
      ) {
        const contractLabels = await getSubscriptionOptionsForOrg();
        setOrgSupraSubscriptionOptions(contractLabels);
      }
    };
    getSupraOwnerSubscriptionOptions();
  }, [auth?.org, hasFeature]);

  const fetchOnboarding = useCallback(async () => {
    const { data } = await Axios.get('/org/onboarding');
    if (data) {
      setOnboardingData(data);
      setLoadingOnboarding(false);
    }
  }, []);

  useEffect(() => {
    (async () => {
      await featureFlagClient.initialize();
      await csrfTokenService.initializeToken();
      await fetchAuth();
    })();
  }, [fetchAuth]);

  /** The current onboarding status of this org. */
  const orgStatus = (auth?.org.orgCustomerSuccess.status as OrgStatus) || null;
  /** The current Plan this brokerage is on. Agent Plan (Tier 1) or Brokerage Plan (Tier 2) */
  const orgPlan = auth?.org.orgCustomerSuccess.currentPlan || null;

  const portalPlanIsLoading =
    auth?.org.onboardingPortalId && !onboardingPortalConfig;
  const upgradePlanDataLoaded = Boolean(
    !fetchingAuth && !portalPlanIsLoading && orgPlan && orgStatus,
  );

  const hasLimitedAccess =
    !auth || isLimitedAccessOnUnclaimedBrokerage(auth, orgStatus, orgPlan);

  const { isOrgPlanUpgraded, isOrgOnBrokeragePlan, isUnclaimedOnAgentPlan } =
    useMemo(() => {
      if (!orgPlan) {
        return {
          // If empty, assume they are on Agent Plan (Tier 1)
          isOrgPlanUpgraded: false,
          isOrgOnBrokeragePlan: false,
          isUnclaimedOnAgentPlan:
            orgStatus === OrgStatus.UNCLAIMED &&
            onboardingPortalConfig?.plan === 'AGENT',
        };
      }

      const { name: currentPlanName, previousPlanName } = orgPlan;

      return {
        isOrgPlanUpgraded:
          currentPlanName === 'BROKERAGE' && previousPlanName === 'AGENT',
        isOrgOnBrokeragePlan:
          currentPlanName === 'BROKERAGE' && previousPlanName === null,
        isUnclaimedOnAgentPlan:
          orgStatus === OrgStatus.UNCLAIMED && currentPlanName === 'AGENT',
      };
    }, [onboardingPortalConfig?.plan, orgPlan, orgStatus]);

  useEffect(() => {
    if (!orgStatus) return;

    if (orgStatus === OrgStatus.ONBOARDING) {
      fetchOnboarding();
    } else {
      setLoadingOnboarding(false);
    }
  }, [orgStatus, fetchOnboarding]);

  useEffect(() => {
    // Initialize chat window
    if (pusher && hasChat && (window as any).BrokerbayChat) {
      // Org has permission to use brokerbay chat, initialize it
      // Wait for chat to initialize for chat inbox links
      try {
        (window as any).BrokerbayChat(pusher);
      } catch (err) {
        errorReporter.error(err);
      }
    }
  }, [hasChat, pusher]);

  if (!auth) return null;

  return (
    <AuthContext.Provider
      value={{
        auth,
        pusher,
        setPusherEvents,
        setAuth,
        logout,
        toggleChatInactive,
        isBrokerBay: Boolean(
          (auth?.isBrokerBay || auth?.assumingUserId) &&
          !auth?.isAssumedNotBB &&
          !auth?.assumerIdMlsStaff,
        ),
        orgStatus,
        orgPlan,
        onboarding: onboardingData,
        offices,
        fetchAuth,
        fetchingAuth,
        fetchOrgStatus: fetchAuth,
        fetchOnboarding,
        answeringPortalConfig,
        onboardingPortalConfig,
        loadingAnsweringPortalConfig,
        loadingOnboarding,
        answeringPortalConfigError,
        hasFeature,
        hasLimitedAccess,
        isFromCountry,
        isOrgPlanUpgraded,
        isOrgOnBrokeragePlan,
        isUnclaimedOnAgentPlan,
        upgradePlanDataLoaded,
        orgSupraSubscriptionOptions,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
